/** What the computer does with the artifacts it has already picked up.
 *
 * It did nothing with them. `evaluate.ts` counted a hero's backpack toward the
 * kingdom's worth and no phase ever issued `EQUIP_ARTIFACT`, so every artifact
 * the AI dug out of a chest sat in the bag for the rest of the game
 * contributing exactly zero: `heroModifiers` reads `equippedSlots(h)`, and an
 * artifact in the bag is in no slot. The computer was carrying its treasure
 * around rather than wearing it.
 *
 * The same phase assembles combination artifacts, which is the other half of
 * the same hole - a set whose parts are never worn is a set that is never
 * noticed - and the reason the fifteen sets in the content had never once been
 * built by anybody.
 *
 * Everything here is a free action: no movement, no gold, no risk. It still
 * costs one of the turn's commands, which is why the scores below are in the
 * same gold-equivalents as every other phase rather than a flat "do it first".
 */

import { freeSlotFor } from "@game/core";
import type { ArtifactDef, ContentRegistry, LegalCommand } from "@game/core";
import type { Shadow } from "./shadow.js";
import type { Candidate } from "./types.js";
import { mainHero } from "./strategy.js";

/**
 * Gold a point of a primary stat is worth.
 *
 * Not a new number: it is the one `objectValue`'s `bonus` arm already prices a
 * +1 Attack shrine at, and using the same one is what makes "wear this helmet"
 * comparable to "walk to that obelisk" without a conversion nobody can check.
 */
export const STAT_GOLD = 3000;
export const MORALE_LUCK_GOLD = 800;

/**
 * What an empty equipment slot is worth once a set has freed it.
 *
 * A slot is only worth anything if there is something to put in it, and the
 * hero who just assembled a five-part relic has usually emptied his bag doing
 * it. Two and a half thousand is the low end of a minor artifact, which is what
 * a freed slot realistically ends up holding.
 */
export const SLOT_FREED_GOLD = 2500;

const PRIMARY = new Set(["attack", "defense", "spellPower", "knowledge"]);

/**
 * An artifact in the currency the rest of the planner trades in.
 *
 * Primaries and morale/luck are counted properly; everything else - sea
 * movement, a granted spell, a resource-per-day - falls back to a tenth of the
 * shop price, because the table's `cost` is the only statement about those
 * effects that exists and a tenth of it keeps a 30000-gold relic ahead of a
 * 1000-gold trinket without pretending the number means more than it does.
 *
 * `add` only. A multiplier's worth depends on what it multiplies, which is not
 * knowable from the artifact alone.
 */
export function artifactWorth(def: ArtifactDef): number {
  let v = 0;
  let priced = false;
  for (const m of def.modifiers) {
    if (m.op !== "add") continue;
    if (PRIMARY.has(m.stat as string)) { v += m.value * STAT_GOLD; priced = true; }
    else if (m.stat === "morale" || m.stat === "luck") {
      v += m.value * MORALE_LUCK_GOLD;
      priced = true;
    }
  }
  return priced ? v : Math.floor(def.cost / 10);
}

/**
 * What putting the set together buys: the artifact's own worth, less the parts
 * that come off, plus the slots they were using.
 *
 * Computed rather than assumed, because the one number that looks like it would
 * answer this does not: Regalia of the Dawn costs 50000 and its five components
 * cost 100000 between them, so a planner that compared shop prices would refuse
 * to build the best artifact in the game. Its modifiers tell the truth - 21 in
 * every primary against the parts' 20, and four slots back.
 */
export function assembleGain(content: ContentRegistry, def: ArtifactDef): number {
  let parts = 0;
  for (const id of def.components) {
    const p = content.artifacts.get(id);
    if (p !== undefined) parts += artifactWorth(p);
  }
  const freed = Math.max(0, def.components.length - 1) * SLOT_FREED_GOLD;
  return artifactWorth(def) - parts + freed;
}

/**
 * How much more a stat is worth on the hero who fights than on the one who
 * found it, in basis points.
 *
 * An artifact only does anything in a battle, and `mainHero` is the hero the
 * kingdom's battles are fought by, so the same helmet is worth more on him
 * than on the scout who dug it up. The margin is what lets "hand it over" beat
 * the scout's own "wear it" when the two are offered side by side, which they
 * always are: the enumerator lists both in one turn.
 */
export const GIVE_TO_MAIN_BP = 12000;

/**
 * Wear what is in the bag, build what can be built, and hand a find to the
 * hero who will use it.
 *
 * `DISASSEMBLE_ARTIFACT` is deliberately not scored. Every set in the content
 * is a strict improvement on its parts, so taking one apart is a losing trade
 * in every position the AI can be in - it exists for a human who wants five
 * slots back, and a phase that offered it would only ever undo the phase above
 * it. That is a decision, not an oversight; if a set is ever authored that is
 * situationally worse than its parts, this is where the other half goes.
 *
 * `UNEQUIP_ARTIFACT` is not scored either, for the mirror reason: the equip
 * arm only wears what `artifactWorth` prices above zero, and no shipped
 * artifact prices below it (the five with a negative primary - the Thunder Colossus's set
 * and the Watcher's Shield - are net positive), so there is never a worn
 * artifact this planner would rather have in the bag. Checked against the
 * content on 2026-09-17; the day a purely cursed artifact ships, the rule is
 * "unequip when worth < 0" and it goes beside the equip arm below.
 *
 * `GIVE_ARTIFACT` is scored one way only: from any hero to `mainHero`, and
 * only when the main hero has a free socket for it, because a gift he can only
 * bag is a command spent moving a trinket from one bag to another. The scout
 * keeps his own set parts (`spokenFor`) for the same reason the equip arm
 * does: assembling takes them straight back.
 */
export function planGear(sh: Shadow, legal: readonly LegalCommand[]): Candidate[] {
  const out: Candidate[] = [];
  const main = mainHero(sh);
  // Parts of a set that can be finished this instant, so the equip arm below
  // can leave them alone. Built from the legal list rather than by asking the
  // artifact table, because "can be assembled right now" is precisely what an
  // enumerated `ASSEMBLE_ARTIFACT` means - and it is per hero, since two heroes
  // can hold halves of two different sets.
  const spokenFor = new Set<string>();
  for (const c of legal) {
    if (c.type !== "ASSEMBLE_ARTIFACT") continue;
    const def = sh.content.artifacts.get(c.artifact);
    if (def === undefined) continue;
    for (const part of def.components) spokenFor.add(`${c.hero as string}:${part as string}`);
  }

  for (const c of legal) {
    if (c.type === "ASSEMBLE_ARTIFACT") {
      const def = sh.content.artifacts.get(c.artifact);
      if (def === undefined) continue;
      const gain = assembleGain(sh.content, def);
      if (gain <= 0) continue;
      out.push({ command: c, score: gain, why: `assemble.${def.rank}` });
      continue;
    }
    if (c.type === "GIVE_ARTIFACT") {
      if (main === null || c.toHero !== main || c.fromHero === main) continue;
      const from = sh.state.heroes[c.fromHero];
      const to = sh.state.heroes[main];
      if (from === undefined || to === undefined) continue;
      const id = from.backpack[c.backpackIndex];
      if (id === undefined) continue;
      const def = sh.content.artifacts.get(id);
      if (def === undefined) continue;
      if (spokenFor.has(`${c.fromHero as string}:${id as string}`)) continue;
      if (freeSlotFor(to, def) === null) continue;
      const worth = artifactWorth(def);
      if (worth <= 0) continue;
      out.push({
        command: c, score: Math.floor((worth * GIVE_TO_MAIN_BP) / 10000), why: `give.${def.slot}`,
      });
      continue;
    }
    if (c.type !== "EQUIP_ARTIFACT") continue;
    const h = sh.state.heroes[c.hero];
    if (h === undefined) continue;
    const id = h.backpack[c.backpackIndex];
    if (id === undefined) continue;
    const def = sh.content.artifacts.get(id);
    if (def === undefined) continue;
    // A component of a set this hero can already finish is left alone: wearing
    // it is a wasted command when assembling takes it straight back off, and
    // the enumerator offers both in the same list.
    if (spokenFor.has(`${c.hero as string}:${id as string}`)) continue;
    const worth = artifactWorth(def);
    if (worth <= 0) continue;
    out.push({ command: c, score: worth, why: `equip.${def.slot}` });
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}
